import React, { useState, useEffect } from 'react';
import PlayService from '../services/PlayService';
import {
  FiPlayCircle,
  FiPlus,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiAward,
  FiUploadCloud,
  FiVideo,
  FiX,
  FiRotateCcw,
  FiExternalLink,
  FiActivity,
} from 'react-icons/fi';

const PlayReels = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showResetModalGame, setShowResetModalGame] = useState(null);
  const [benchmarkScore, setBenchmarkScore] = useState(100);
  const [toastMessage, setToastMessage] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    tagline: '',
    category: 'Arcade',
    metricType: 'SCORE', // SCORE or LEVEL
    metricUnit: 'Points',
    startingRecord: '500',
    videoUrlInput: '',
    directGameUrl: '',
  });
  const [gameZipFile, setGameZipFile] = useState(null);
  const [previewVideoFile, setPreviewVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchGames();
  }, []);

  const showToast = (msg, isError = false) => {
    setToastMessage({ text: msg, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await PlayService.getAllAdmin();
      const list = Array.isArray(res) ? res : (res.games || res.data || []);
      setGames(list);
    } catch (err) {
      console.error('Error fetching admin playables:', err);
      showToast('Failed to load playable games', true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (game) => {
    setTogglingId(game._id);
    const prevStatus = game.isLive;

    // Optimistic UI update
    setGames((prev) =>
      prev.map((g) => (g._id === game._id ? { ...g, isLive: !prevStatus } : g))
    );

    try {
      const res = await PlayService.toggleGame(game._id);
      showToast(res.message || 'Game status updated');
      if (res.game) {
        setGames((prev) =>
          prev.map((g) => (g._id === game._id ? { ...g, ...res.game } : g))
        );
      }
    } catch (err) {
      console.error('Error toggling game:', err);
      showToast('Failed to toggle game', true);
      // Revert
      setGames((prev) =>
        prev.map((g) => (g._id === game._id ? { ...g, isLive: prevStatus } : g))
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleResetRecord = async (e) => {
    e.preventDefault();
    if (!showResetModalGame) return;

    try {
      const res = await PlayService.resetRecord(showResetModalGame._id, benchmarkScore);
      showToast(res.message || 'Champion record reset successfully');
      if (res.game) {
        setGames((prev) =>
          prev.map((g) => (g._id === showResetModalGame._id ? { ...g, ...res.game } : g))
        );
      }
      setShowResetModalGame(null);
    } catch (err) {
      console.error('Error resetting record:', err);
      showToast('Failed to reset record', true);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      showToast('Please enter a game title', true);
      return;
    }
    if (!gameZipFile && !formData.directGameUrl) {
      showToast('Please upload a Game ZIP or enter direct game URL', true);
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('tagline', formData.tagline);
    data.append('category', formData.category);
    data.append('metricType', formData.metricType);
    data.append('metricUnit', formData.metricUnit);
    data.append('startingRecord', formData.startingRecord);
    data.append('videoUrlInput', formData.videoUrlInput);
    data.append('directGameUrl', formData.directGameUrl);

    if (gameZipFile) data.append('gameZip', gameZipFile);
    if (previewVideoFile) data.append('previewVideo', previewVideoFile);

    try {
      const res = await PlayService.uploadGame(data);
      showToast(res.message || 'Game uploaded successfully!');
      setShowUploadModal(false);
      // Reset form
      setFormData({
        title: '',
        tagline: '',
        category: 'Arcade',
        metricType: 'SCORE',
        metricUnit: 'Points',
        startingRecord: '500',
        videoUrlInput: '',
        directGameUrl: '',
      });
      setGameZipFile(null);
      setPreviewVideoFile(null);
      fetchGames();
    } catch (err) {
      console.error('Error uploading game:', err);
      showToast(err.response?.data?.message || 'Failed to upload game', true);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPlaysCount = games.reduce((acc, curr) => acc + (curr.totalPlays || 0), 0);
  const liveCount = games.filter((g) => g.isLive).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Toast */}
      {toastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold text-white transition-all ${
            toastMessage.isError ? 'bg-red-600' : 'bg-emerald-600'
          }`}
        >
          {toastMessage.isError ? <FiAlertCircle className="w-5 h-5" /> : <FiCheckCircle className="w-5 h-5" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-tr from-purple-600 to-pink-500 text-white rounded-xl shadow-md">
            <FiPlayCircle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Play Reels & HTML5 Games Feed</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Upload games (ZIP), attach demo videos, and control the vertical game reel feed in the mobile app.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchGames}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-semibold shadow-xs transition"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-md transition"
          >
            <FiPlus className="w-4 h-4" />
            Upload New Game
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Games in Reel</p>
            <p className="text-2xl font-black text-slate-800 mt-1">{games.length}</p>
          </div>
          <div className="w-11 h-11 bg-purple-100 rounded-xl flex items-center justify-center text-purple-700 font-bold text-lg">
            🎮
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Live in Mobile App</p>
            <p className="text-2xl font-black text-emerald-600 mt-1">{liveCount}</p>
          </div>
          <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Total Plays</p>
            <p className="text-2xl font-black text-indigo-600 mt-1">{totalPlaysCount.toLocaleString()}</p>
          </div>
          <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
            <FiActivity className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Games Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-800">Active Game Reels</h2>
          <span className="text-xs text-slate-500">{games.length} games available</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <FiRefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-2" />
            <p className="text-sm">Loading playable game feed...</p>
          </div>
        ) : games.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <FiPlayCircle className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-base font-bold text-slate-700">No games found</p>
            <p className="text-xs text-slate-400 mt-1">Upload your first game to start the vertical video reel feed!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {games.map((game) => {
              const isLive = game.isLive === true;
              const isScoreType = game.metricType === 'SCORE';

              return (
                <div key={game._id} className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-slate-50/70 transition">
                  {/* Left: Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-24 bg-slate-900 rounded-xl overflow-hidden shrink-0 shadow-sm border border-slate-200 flex items-center justify-center relative">
                      {game.videoUrl ? (
                        <video src={game.videoUrl} className="w-full h-full object-cover" muted autoPlay loop playsInline />
                      ) : (
                        <span className="text-2xl">🎮</span>
                      )}
                      <span className="absolute bottom-1 right-1 bg-black/70 text-[9px] text-white px-1 rounded font-bold">
                        9:16
                      </span>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-extrabold text-slate-900 text-base">{game.title}</h3>
                        <span className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 text-[10px] font-bold uppercase">
                          {game.category || 'Arcade'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                          isScoreType ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {isScoreType ? 'Points Based' : 'Level Based'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{game.tagline || 'Vertical Tapcoe Play Game'}</p>

                      {/* Champion Badge */}
                      <div className="mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200/80 text-amber-900 text-xs font-bold shadow-2xs">
                        <FiAward className="w-4 h-4 text-amber-600" />
                        <span>👑 Champion: <span className="text-purple-700 font-extrabold">{game.currentChampion?.userName || 'Tapcoe Master'}</span></span>
                        <span className="bg-amber-200/70 px-2 py-0.5 rounded-md text-[11px] font-black text-amber-900">
                          {game.currentChampion?.displayRecord || `${game.currentChampion?.recordValue} ${game.metricUnit}`}
                        </span>
                      </div>

                      <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-4">
                        <span>Total Plays: <strong className="text-slate-700">{game.totalPlays || 0}</strong></span>
                        <span>Likes: <strong className="text-slate-700">{game.likesCount || 0}</strong></span>
                        <a href={game.gameFolderUrl} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline flex items-center gap-1 font-semibold">
                          Open Game Link <FiExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                    <button
                      onClick={() => {
                        setShowResetModalGame(game);
                        setBenchmarkScore(game.currentChampion?.recordValue || 100);
                      }}
                      className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-xs font-semibold text-slate-700 flex items-center gap-1.5 shadow-2xs transition"
                      title="Reset Champion Record"
                    >
                      <FiRotateCcw className="w-3.5 h-3.5 text-slate-500" />
                      Reset Record
                    </button>

                    {/* Toggle Switch */}
                    <div className="flex flex-col items-end">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isLive}
                          disabled={togglingId === game._id}
                          onChange={() => handleToggle(game)}
                          className="sr-only peer"
                        />
                        <div
                          className={`w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            isLive ? '!bg-emerald-500' : 'bg-slate-300'
                          }`}
                        ></div>
                      </label>
                      <span className={`text-[10px] font-black mt-0.5 ${isLive ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {isLive ? 'LIVE' : 'OFF'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload New Game Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 shadow-2xl border border-slate-200 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowUploadModal(false)}
              className="absolute top-5 right-5 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"
            >
              <FiX className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-xl">
                🚀
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Upload Playable Game</h2>
                <p className="text-xs text-slate-500">ZIP automatically unzips & deploys to high-speed CDN</p>
              </div>
            </div>

            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Game Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Neon Tap Runner"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Arcade">Arcade</option>
                    <option value="Puzzle">Puzzle</option>
                    <option value="Runner">Runner</option>
                    <option value="Racing">Racing</option>
                    <option value="Sports">Sports</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Metric Type *</label>
                  <select
                    value={formData.metricType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setFormData({
                        ...formData,
                        metricType: nextType,
                        metricUnit: nextType === 'LEVEL' ? 'Level' : 'Points',
                      });
                    }}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="SCORE">Score / High Score (Points)</option>
                    <option value="LEVEL">Levels / Stages (Stage Progression)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Metric Unit</label>
                  <input
                    type="text"
                    placeholder="e.g. Points, Level, Meters"
                    value={formData.metricUnit}
                    onChange={(e) => setFormData({ ...formData, metricUnit: e.target.value })}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Initial Target to Beat</label>
                  <input
                    type="number"
                    placeholder="e.g. 500"
                    value={formData.startingRecord}
                    onChange={(e) => setFormData({ ...formData, startingRecord: e.target.value })}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Upload Game ZIP */}
              <div className="p-4 rounded-2xl bg-purple-50/60 border-2 border-dashed border-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <FiUploadCloud className="w-5 h-5 text-purple-600" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Upload HTML5 Game ZIP (.zip)
                  </span>
                </div>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setGameZipFile(e.target.files[0])}
                  className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-purple-600 file:text-white hover:file:bg-purple-700 cursor-pointer"
                />
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Package must contain <code>index.html</code> at root. Auto-unzips on upload.
                </p>
              </div>

              {/* Upload Demo Video */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <FiVideo className="w-5 h-5 text-slate-700" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Demo Preview Video (9:16 Vertical MP4)
                  </span>
                </div>
                <input
                  type="file"
                  accept="video/mp4"
                  onChange={(e) => setPreviewVideoFile(e.target.files[0])}
                  className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-700 file:text-white hover:file:bg-slate-800 cursor-pointer"
                />
                <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2">
                  <span>OR Paste Video URL:</span>
                  <input
                    type="url"
                    placeholder="https://.../preview.mp4"
                    value={formData.videoUrlInput}
                    onChange={(e) => setFormData({ ...formData, videoUrlInput: e.target.value })}
                    className="flex-1 px-2.5 py-1 text-xs rounded-lg border border-slate-200"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-md transition disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {submitting ? 'Uploading & Unzipping...' : 'Upload & Deploy Game'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Record Modal */}
      {showResetModalGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-200 relative">
            <button
              onClick={() => setShowResetModalGame(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600"
            >
              <FiX className="w-5 h-5" />
            </button>

            <h3 className="font-bold text-slate-900 text-base mb-1">Reset Champion Record</h3>
            <p className="text-xs text-slate-500 mb-4">
              Reset the current champion of <strong>{showResetModalGame.title}</strong> back to benchmark.
            </p>

            <form onSubmit={handleResetRecord} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">New Benchmark Record</label>
                <input
                  type="number"
                  value={benchmarkScore}
                  onChange={(e) => setBenchmarkScore(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowResetModalGame(null)}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md"
                >
                  Reset Champion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayReels;

